import { ChevronRight, ClipboardList, Download, FileText, Loader2 } from 'lucide-react'
import { useState } from 'react'

import type {
  PatientFile,
  PatientFileNoteSource,
  PatientFileSource,
  PatientFileTaskSource,
} from '@snootsvet/shared'
import { NOTE_TYPE_LABELS, TASK_STATUS_LABELS } from '@snootsvet/shared'

import { FileViewerDialog } from '@/frontend/components/file-viewer-dialog'
import { FormattedDate, FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/frontend/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { TASK_STATUS_VARIANT } from '@/frontend/features/tasks/task-utils'
import { useDownloadPatientFiles } from '@/frontend/hooks/api/patients/use-download-patient-files'
import { useListPatientFiles } from '@/frontend/hooks/api/patients/use-list-patient-files'
import { useFileDownloads } from '@/frontend/hooks/use-file-downloads'
import { cn } from '@/frontend/lib/utils'

/** Number of note files shown inline on the card before the "View all" affordance. */
const CARD_PREVIEW_LIMIT = 5

/** Formats a byte count as a compact human-readable size (e.g. "1.4 MB"). Returns null if unknown. */
function formatFileSize(bytes: number | null): string | null {
  if (bytes == null) return null
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let size = bytes / 1024
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unitIndex]}`
}

/**
 * Sidebar card listing a patient's files. Shows recent note/consultation files
 * inline with a total count badge; the full dialog lists every file (notes and
 * tasks) with its originating context. Clicking a file opens an inline viewer
 * that supports arrow-key paging, and files can be downloaded individually or
 * all at once.
 */
export function PatientFilesCard({ patientId }: { patientId: string }) {
  const { data: files } = useListPatientFiles(patientId)
  const [dialogOpen, setDialogOpen] = useState(false)
  // Index into `allFiles` of the file open in the viewer, or null when closed.
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)
  const { downloadFile } = useFileDownloads()
  const { download: downloadAll, isDownloading: isDownloadingAll } =
    useDownloadPatientFiles(patientId)

  const allFiles = files ?? []
  const previewFiles = allFiles.slice(0, CARD_PREVIEW_LIMIT)

  return (
    <Card>
      <CardHeader className="cursor-pointer" onClick={() => setDialogOpen(true)}>
        <CardTitle className="flex items-center gap-2">
          Files
          {allFiles.length > 0 && (
            <Badge variant="secondary" size="sm">
              {allFiles.length}
            </Badge>
          )}
        </CardTitle>
        <CardAction>
          <ChevronRight className="size-4 text-muted-foreground" />
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-3">
        {allFiles.length === 0 ? (
          <p className="text-muted-foreground">No files.</p>
        ) : (
          <div className="space-y-2">
            {previewFiles.map((file, index) => (
              <div key={file.id} className="flex items-start gap-2">
                <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <FileNameButton
                    fileName={file.fileName}
                    onClick={() => setViewerIndex(index)}
                    className="block break-words"
                  />
                  <p className="truncate text-sm text-muted-foreground">
                    {fileSourceLabel(file.source)} ·{' '}
                    <FormattedDate value={file.createdAt} clinicId={null} />
                  </p>
                </div>
                <FileDownloadButton onClick={() => downloadFile(file)} />
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {allFiles.length > 0 && (
        <CardFooter className="justify-end">
          <DownloadAllButton
            onClick={() => downloadAll()}
            isDownloading={isDownloadingAll}
            count={allFiles.length}
          />
        </CardFooter>
      )}

      <PatientFilesDialog
        files={allFiles}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onView={setViewerIndex}
        onDownload={downloadFile}
        onDownloadAll={() => downloadAll()}
        isDownloadingAll={isDownloadingAll}
      />

      <FileViewerDialog
        files={allFiles}
        index={viewerIndex}
        onIndexChange={setViewerIndex}
        onClose={() => setViewerIndex(null)}
      />
    </Card>
  )
}

/** Underlined filename button that opens the file in the viewer. */
function FileNameButton({
  fileName,
  onClick,
  className,
}: {
  fileName: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(
        'cursor-pointer text-left text-primary underline underline-offset-2',
        className
      )}
      onClick={onClick}
    >
      {fileName}
    </button>
  )
}

/** Compact icon button that downloads a single file straight to the device. */
function FileDownloadButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label="Download file"
      className="shrink-0 text-muted-foreground"
      onClick={onClick}
    >
      <Download />
    </Button>
  )
}

/** Button that downloads every file in the card; shows a spinner while running. */
function DownloadAllButton({
  onClick,
  isDownloading,
  count,
}: {
  onClick: () => void
  isDownloading: boolean
  count?: number
}) {
  return (
    <Button variant="outline" onClick={onClick} disabled={isDownloading}>
      {isDownloading ? <Loader2 className="animate-spin" /> : <Download />}
      Download all{count != null ? ` (${count})` : ''}
    </Button>
  )
}

/** Short label describing a file's source for the card inline list. */
function fileSourceLabel(source: PatientFileSource): string {
  if (source.type === 'task') return `Task · ${source.taskTitle}`
  const typeLabel = NOTE_TYPE_LABELS[source.noteType] ?? source.noteType
  if (source.consultation?.appointment?.appointmentTypeName) {
    return `${typeLabel} · ${source.consultation.appointment.appointmentTypeName}`
  }
  if (source.consultation) return `${typeLabel} · Consultation`
  return typeLabel
}

/** Dialog listing every file for a patient with its originating context. */
function PatientFilesDialog({
  files,
  open,
  onOpenChange,
  onView,
  onDownload,
  onDownloadAll,
  isDownloadingAll,
}: {
  files: PatientFile[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onView: (index: number) => void
  onDownload: (file: PatientFile) => void
  onDownloadAll: () => void
  isDownloadingAll: boolean
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader className="flex flex-row items-center justify-between gap-3 pr-12">
          <DialogTitle>Files ({files.length})</DialogTitle>
          {files.length > 0 && (
            <DownloadAllButton onClick={onDownloadAll} isDownloading={isDownloadingAll} />
          )}
        </DialogHeader>
        {files.length === 0 ? (
          <p className="text-muted-foreground">No files.</p>
        ) : (
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li key={file.id}>
                <FileRow
                  file={file}
                  onView={() => onView(index)}
                  onDownload={() => onDownload(file)}
                />
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** A single file row in the dialog — filename, source badge, context, and actions. */
function FileRow({
  file,
  onView,
  onDownload,
}: {
  file: PatientFile
  onView: () => void
  onDownload: () => void
}) {
  const isNote = file.source.type === 'note'
  const fileSize = formatFileSize(file.sizeBytes)
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-start gap-2">
        <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <FileNameButton fileName={file.fileName} onClick={onView} className="truncate" />
            {!isNote && (
              <Badge variant="outline" size="sm">
                <ClipboardList />
                Task
              </Badge>
            )}
          </div>

          {isNote ? (
            <NoteContext source={file.source as PatientFileNoteSource} />
          ) : (
            <TaskContext source={file.source as PatientFileTaskSource} />
          )}

          <p className="text-sm text-muted-foreground">
            <FormattedDateTime value={file.createdAt} clinicId={null} />
            {file.uploadedByStaff &&
              ` · ${file.uploadedByStaff.firstName} ${file.uploadedByStaff.lastName}`}
            {fileSize && ` · ${fileSize}`}
          </p>
        </div>
        <FileDownloadButton onClick={onDownload} />
      </div>
    </div>
  )
}

/** Context block for a note-sourced file — note type, consultation/appointment, content preview. */
function NoteContext({ source }: { source: PatientFileNoteSource }) {
  const typeLabel = NOTE_TYPE_LABELS[source.noteType] ?? source.noteType
  const appointment = source.consultation?.appointment
  return (
    <div className="space-y-0.5 text-sm">
      <p>
        <span className="font-medium">{typeLabel} note</span>
        {appointment ? (
          <span className="text-muted-foreground">
            {' · '}
            {appointment.appointmentTypeName} on{' '}
            <FormattedDate value={appointment.startTime} clinicId={appointment.clinicId} />
          </span>
        ) : source.consultation ? (
          <span className="text-muted-foreground"> · Consultation</span>
        ) : null}
      </p>
      {source.contentPreview && (
        <p className="text-muted-foreground line-clamp-2">{source.contentPreview}</p>
      )}
    </div>
  )
}

/** Context block for a task-sourced file — task title and status. */
function TaskContext({ source }: { source: PatientFileTaskSource }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="font-medium">{source.taskTitle}</span>
      <Badge variant={TASK_STATUS_VARIANT[source.taskStatus]} size="sm">
        {TASK_STATUS_LABELS[source.taskStatus]}
      </Badge>
    </div>
  )
}
